import React from 'react';
import AITimeSeriesChart from './charts/AITimeSeriesChart';
import AIDistributionChart from './charts/AIDistributionChart';
import AIPercentileChart from './charts/AIPercentileChart';
import AIDataTable from './charts/AIDataTable';

// ── AI Insights card renderers (shared by FleetAnalyticsDashboard and
// MbbTelemetryAIInsights). All markup is scoped under `.fho-dashboard` in
// FleetAnalytics.css, so consumers must render these inside that wrapper. ──

export function renderMetricsCard(card, icon, title, iconColor) {
    if (!card || !card.primary_metric || !card.secondary_metric || !card.range_metric || !card.count_metric) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className={`ai-card-icon ${iconColor}`}>{icon}</div>
                <div className="ai-card-title">{title}</div>
            </div>
            <div className="ai-metrics-stats">
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.primary_metric.color}`}>{card.primary_metric.value}</span>
                    <span className="ai-stat-label">{card.primary_metric.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.secondary_metric.color}`}>{card.secondary_metric.value}</span>
                    <span className="ai-stat-label">{card.secondary_metric.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.range_metric.color}`}>{card.range_metric.value}</span>
                    <span className="ai-stat-label">{card.range_metric.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.count_metric.color}`}>{card.count_metric.value}</span>
                    <span className="ai-stat-label">{card.count_metric.label}</span>
                </div>
            </div>
        </div>
    );
}

export function renderExtremesCard(card) {
    if (!card || !card.items) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className="ai-card-icon warning-bg">{card.icon}</div>
                <div className="ai-card-title">{card.title}</div>
            </div>
            <ul className="ai-item-list">
                {card.items.map((item, i) => (
                    <li key={i} className="ai-item">
                        <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
                        <div className="ai-item-content">
                            <div className="ai-item-label">{item.label}<span className="ai-item-value">{item.value}</span></div>
                            <div className="ai-item-details">{item.details}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function renderAnomaliesCard(card) {
    if (!card || !card.items) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className="ai-card-icon danger-bg">{card.icon}</div>
                <div className="ai-card-title">{card.title}</div>
            </div>
            <ul className="ai-item-list">
                {card.items.map((item, i) => (
                    <li key={i} className="ai-item">
                        <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
                        <div className="ai-item-content">
                            <div className="ai-item-label">{item.label}<span className="ai-item-value">{item.value}</span></div>
                            <div className="ai-item-details">{item.description}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function renderTrendsCard(card) {
    if (!card) return null;
    const { chart_type, chart_data, chart_labels, table_data, icon, title, trend_description, details } = card;
    const header = (
        <div className="ai-card-header">
            <div className="ai-card-icon primary-bg">{icon}</div>
            <div className="ai-card-title">{title}</div>
        </div>
    );
    const desc = (
        <>
            <div className="ai-trend-description"><strong>Trend:</strong> {trend_description}</div>
            <div className="ai-trend-details">{details}</div>
        </>
    );
    switch (chart_type) {
        case 'time-series':
            return <div className="ai-card">{header}{desc}<AITimeSeriesChart title="" chartData={chart_data} chartLabels={chart_labels} yAxisLabel="Value" /></div>;
        case 'distribution':
            return <div className="ai-card">{header}{desc}<AIDistributionChart title="" chartData={chart_data} chartType="auto" /></div>;
        case 'percentile':
            return <div className="ai-card">{header}{desc}<AIPercentileChart title="" chartData={chart_data} /></div>;
        case 'table':
            return <div className="ai-card">{header}{desc}<AIDataTable title="" tableData={table_data} /></div>;
        case 'none':
            return <div className="ai-card">{header}{desc}</div>;
        default: {
            const hasData = chart_data && Array.isArray(chart_data) && chart_data.length > 0;
            const isOldFormat = hasData && typeof chart_data[0] === 'number';
            if (isOldFormat) {
                const maxVal = Math.max(...chart_data);
                return (
                    <div className="ai-card">{header}{desc}
                        <div className="ai-chart-container">
                            {chart_data.slice(0, 100).map((v, i) => (
                                <div key={i} className="ai-chart-bar" style={{ height: `${(v / maxVal) * 150}px` }} title={`${v}`}></div>
                            ))}
                        </div>
                    </div>
                );
            }
            return <div className="ai-card">{header}{desc}<AITimeSeriesChart title="" chartData={chart_data} chartLabels={chart_labels} yAxisLabel="Value" /></div>;
        }
    }
}

export function renderPatternsCard(card) {
    if (!card || !card.items) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className="ai-card-icon warning-bg">{card.icon}</div>
                <div className="ai-card-title">{card.title}</div>
            </div>
            <ul className="ai-item-list">
                {card.items.map((item, i) => (
                    <li key={i} className="ai-item">
                        <div className={`ai-item-bullet ${item.indicator_color}-bg`}></div>
                        <div className="ai-item-content">
                            <div className="ai-item-label">{item.label}<span className="ai-item-value">{item.value}</span></div>
                            <div className="ai-item-details">{item.description}</div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function renderBaselineCard(card) {
    if (!card || !card.primary_stat || !card.secondary_stat) return null;
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className="ai-card-icon primary-bg">{card.icon}</div>
                <div className="ai-card-title">{card.title}</div>
            </div>
            <div className="ai-baseline-range"><strong>Normal Operating Range:</strong> {card.normal_operating_range}</div>
            <div className="ai-baseline-description">{card.range_description}</div>
            <div className="ai-baseline-stats">
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.primary_stat.color}`}>{card.primary_stat.value}</span>
                    <span className="ai-stat-label">{card.primary_stat.label}</span>
                </div>
                <div className="ai-stat-item">
                    <span className={`ai-stat-value ${card.secondary_stat.color}`}>{card.secondary_stat.value}</span>
                    <span className="ai-stat-label">{card.secondary_stat.label}</span>
                </div>
            </div>
        </div>
    );
}

export function renderRecoveryHierarchyCard() {
    const levels = [
        { level: 0, name: 'NO_ACTION',      description: 'No recovery attempted',                      severity: 'success' },
        { level: 1, name: 'MODEM_RLF',      description: 'Radio Link Failure recovery — least invasive', severity: 'success' },
        { level: 2, name: 'PDN_TOGGLE',     description: 'Packet Data Network reset — moderate',         severity: 'warning' },
        { level: 3, name: 'LPM_TOGGLE',     description: 'Low Power Mode cycle — moderate',              severity: 'warning' },
        { level: 4, name: 'MODEM_SSR',      description: 'Modem Subsystem Restart — aggressive',         severity: 'danger' },
        { level: 5, name: 'DEVICE_REBOOT',  description: 'Full device restart — very aggressive',        severity: 'danger' },
        { level: 6, name: 'FACTORY_RESET',  description: 'Complete device reset — most extreme',         severity: 'danger' },
    ];
    return (
        <div className="ai-card">
            <div className="ai-card-header">
                <div className="ai-card-icon primary-bg">🔄</div>
                <div className="ai-card-title">Recovery Action Hierarchy</div>
            </div>
            <ol className="ai-recovery-list">
                {levels.map((item, i) => (
                    <li key={i} className="ai-recovery-item">
                        <div className={`ai-recovery-bullet ${item.severity}-bg`}></div>
                        <div className="ai-recovery-content">
                            <div className="ai-recovery-label">{item.name}<span className="ai-recovery-level">(Level {item.level})</span></div>
                            <div className="ai-recovery-description">{item.description}</div>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}

export function renderCacheInfo(metadata, deviceCount, viewType) {
    if (!metadata && deviceCount == null) return null;
    const deviceChip = deviceCount != null ? (
        <span className="ai-device-count-chip">{deviceCount} device{deviceCount !== 1 ? 's' : ''}</span>
    ) : null;
    if (!metadata) return deviceChip ? <div className="ai-cache-info"><div className="ai-cache-info-content"><div className="ai-cache-info-left">{deviceChip}</div></div></div> : null;
    const { cached_at, data_time_range, is_stale, status, warning } = metadata;
    const formatRelative = (ts) => {
        if (!ts) return '';
        const diff = Date.now() - new Date(ts).getTime();
        const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
        if (m < 1) return 'just now';
        if (m < 60) return `${m} minute${m !== 1 ? 's' : ''} ago`;
        if (h < 24) return `${h} hour${h !== 1 ? 's' : ''} ago`;
        return `${d} day${d !== 1 ? 's' : ''} ago`;
    };
    const formatRange = (r) => {
        if (!r || !r.from || !r.to) return '';
        const opts = { month: 'short', day: 'numeric', year: 'numeric' };
        return `${new Date(r.from).toLocaleDateString('en-US', opts)} - ${new Date(r.to).toLocaleDateString('en-US', opts)}`;
    };
    return (
        <div className={`ai-cache-info${is_stale ? ' stale' : ''}`}>
            <div className="ai-cache-info-content">
                <div className="ai-cache-info-left">
                    <span className={`ai-cache-status-dot ${status === 'ready' ? 'ready' : 'warning'}`}></span>
                    <span className="ai-cache-info-text"><strong>Last Updated:</strong> {formatRelative(cached_at)}</span>
                    {data_time_range && <span className="ai-cache-info-text ai-cache-info-separator"><strong>Data Range:</strong> {formatRange(data_time_range)}</span>}
                    {deviceChip}
                </div>
                {warning && (
                    <div className="ai-cache-info-right">
                        <span className="ai-cache-warning">⚠️ {warning}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── AI Insights error card ──
export function AIErrorCard({ error, onRetry }) {
    if (!error) return null;
    return (
        <div className="ai-error-card" style={{ marginBottom: 12 }}>
            <div className="ai-error-icon">
                {error.type === 'warming' ? '⏳' : error.type === 'not_found' ? '🔍' : '⚠️'}
            </div>
            <h3>{error.type === 'warming' ? 'Generating Insights…' : error.type === 'not_found' ? 'Data Not Found' : error.type === 'timeout' ? 'Request Timed Out' : 'Error Loading Insights'}</h3>
            <p>{error.message}</p>
            {error.warning && <p className="ai-error-warning">⚠️ {error.warning}</p>}
            {error.type !== 'warming' && onRetry && <button className="ai-retry-btn" onClick={onRetry}>Retry</button>}
        </div>
    );
}
